import {Component, Input} from '@angular/core';
import {CpNameResolution} from '../cp-provision-object-data/cp-provision-object-data.component';

/**
 * navItem "Basisdaten" of the Provisionssatz OBC, widget alias `cp.contractAgent.baseData`.
 *
 * The IDENTITY of a Provisionssatz: the Abrechnungskonto and the Beraterrolle it was produced for,
 * the Provisionsart, and where the row came from (`source` AUTOMATIC|MANUAL plus
 * `agentManuallyChanged`). Then the denormalised keys that let a caseworker read the row without
 * navigating away — contract, Provisionsart-Baum, Ereignis, and the matrix the run picked.
 *
 * FORM MECHANISM: metadata driven, no manual Angular form — the fields, their German labels and
 * their order come from the backend FormObject of `cp.ProvisionContractAgent`; this component
 * only names the visible subset. See {@link CpProvisionObjectDataComponent}.
 *
 * ONE DEFERRED FIELD: `provisionTypeMatrix` (:380, `@ManyToOne(optional = true)`) is nullable
 * without a default and is resolved by `ProvisionCalculationService` alone — it is the matrix the
 * run PICKED, not a key anyone states. It therefore carries "nicht berechnet" while empty, with
 * the block-level explanation once above the block. The other denormalised keys are copied down
 * from the Provisionszeile when the row is created and are NOT deferred.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * TWO FIELDS DO NOT CARRY A NAME AND ARE RESOLVED ONE HOP FURTHER
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * `provisionAgentDtoName` and `contractDtoName` both come back as the raw ID, because neither FK
 * target has a usable name (see {@link CpNameResolution} for the mechanism, and
 * {@link nameResolutions} below for what each one is read from). Measured on the running E2E
 * stack before the fix: "1" instead of "E2E Test Agent", and "990001122" instead of "VN-430-03".
 *
 * `provisionContractDtoName` was REMOVED from the visible set rather than resolved, and that is a
 * decision worth stating: `cp.ProvisionContract` has no name column, does not implement
 * `EntityNameGet` and has no `getName()` at all, so there is no name to walk to — the value is
 * always the id. A Provisionszeile is identified by contract + Provisionsart + Ereignis + period,
 * and all three of the first are already rendered right here, while the navItem "Vertrag" shows
 * the Provisionszeile itself in full. A bare id under the label "Vertragsprovision" would be the
 * exact misleading cell this whole change removes, and it would carry information the user cannot
 * act on. Reversible: re-add the field and give it a `(ID)` label if the id is wanted as a support
 * handle.
 */
@Component({
    selector: 'cp-provision-contract-agent-base-data',
    templateUrl: './cp-provision-contract-agent-base-data.component.html',
    styleUrls: ['./cp-provision-contract-agent-base-data.component.scss'],
    standalone: false
})
export class CpProvisionContractAgentBaseDataComponent {

    @Input() provisionContractAgentId: number;

    /** Fields per row — the side view passes 1. */
    @Input() columns: number = 2;

    /**
     * Visible fields, in the order of the requirement. FK fields are addressed by their
     * `...DtoName` variant: the `...DtoId` variant carries a `relationship` and is skipped by the
     * widget layout compiler (`widget.service.ts:993-996`).
     */
    readonly fields: string[] = [
        'provisionAgentDtoName',
        'agentRole',
        'provisionTypeComponentDtoName',
        'source',
        'agentManuallyChanged',
        'contractDtoName',
        'provisionTypeDtoName',
        'provisionTypeContractEventDtoName',
        'provisionTypeMatrixDtoName'
    ];

    /**
     * The single deferred field of this pane — the matrix the run resolved.
     * Addressed by its `...DtoName` variant, because that is the field the widget renders.
     */
    readonly pendingFields: string[] = [
        'provisionTypeMatrixDtoName'
    ];

    /**
     * The two fields whose `...DtoName` is an id, and where the real name lives.
     *
     *   provisionAgentDtoName -> cp.ProvisionAgent.agentDtoName   ("E2E Test Agent")
     *       the Abrechnungskonto has no name of its own; the Berater behind it does.
     *   contractDtoName       -> cm.Contract.contractIdentifier   ("VN-430-03")
     *       cm.Contract has no name column either; its business label is the Vertragsnummer.
     *       This is the same field the navItem "Vertrag" already renders, so both surfaces now
     *       say the same thing about the same contract.
     *
     * Both cost one light read. If either cannot be resolved, the cell stays EMPTY — never the id.
     */
    readonly nameResolutions: CpNameResolution[] = [
        {
            field: 'provisionAgentDtoName',
            idField: 'provisionAgentDtoId',
            objectType: 'cp.ProvisionAgent',
            nameField: 'agentDtoName'
        },
        {
            field: 'contractDtoName',
            idField: 'contractDtoId',
            objectType: 'cm.Contract',
            nameField: 'contractIdentifier'
        }
    ];

}
